import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { RecentlyViewedProvider } from "@/contexts/RecentlyViewedContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieConsent from "@/components/storefront/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";

// Storefront pages — eagerly imported (most visitors land here)
import StoreLayout from "@/components/storefront/StoreLayout";
import Home from "@/pages/storefront/Home";
import Catalog from "@/pages/storefront/Catalog";
import Product from "@/pages/storefront/Product";
import Cart from "@/pages/storefront/Cart";
import Checkout from "@/pages/storefront/Checkout";
import Wishlist from "@/pages/storefront/Wishlist";
import BrandsList from "@/pages/storefront/BrandsList";
import ContentPage from "@/pages/storefront/ContentPage";
import Contact from "@/pages/storefront/Contact";
import FAQ from "@/pages/storefront/FAQ";
import ThankYou from "@/pages/storefront/ThankYou";
import NotFound from "@/pages/storefront/NotFound";
import AccountLogin from "@/pages/storefront/AccountLogin";
import AccountRegister from "@/pages/storefront/AccountRegister";
import AccountForgot from "@/pages/storefront/AccountForgot";
import AccountReset from "@/pages/storefront/AccountReset";
import AccountLayout from "@/pages/storefront/AccountLayout";
import AccountOverview from "@/pages/storefront/AccountOverview";
import AccountOrders, { AccountOrderDetail } from "@/pages/storefront/AccountOrders";
import AccountAddresses from "@/pages/storefront/AccountAddresses";
import AccountSecurity from "@/pages/storefront/AccountSecurity";

// Admin pages — lazy-loaded (chunk only fetched when an admin route opens)
const AppLayout = lazy(() => import("@/components/layout/AppLayout"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Products = lazy(() => import("@/pages/Products"));
const ProductEdit = lazy(() => import("@/pages/ProductEdit"));
const Categories = lazy(() => import("@/pages/Categories"));
const Brands = lazy(() => import("@/pages/Brands"));
const ImportWizard = lazy(() => import("@/pages/Import"));
const ImportHistory = lazy(() => import("@/pages/ImportHistory"));
const Users = lazy(() => import("@/pages/Users"));
const Logs = lazy(() => import("@/pages/Logs"));
const Settings = lazy(() => import("@/pages/Settings"));
const Orders = lazy(() => import("@/pages/Orders"));
const OrderEdit = lazy(() => import("@/pages/OrderEdit"));
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerEdit = lazy(() => import("@/pages/CustomerEdit"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const PromoCodes = lazy(() => import("@/pages/PromoCodes"));
const Tools = lazy(() => import("@/pages/Tools"));

// Lightweight loading fallback (shown briefly while the admin chunk loads)
function AdminLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F2EDDF]" data-testid="admin-loading">
      <div className="flex items-center gap-3 text-sm text-[#2D2823]">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#FF5A1F]" />
        <span>Loading admin…</span>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-right" richColors closeButton />
      <CartProvider>
        <WishlistProvider>
          <RecentlyViewedProvider>
            <CustomerAuthProvider>
              <AuthProvider>
              <Routes>
              {/* PUBLIC STOREFRONT */}
              <Route path="/" element={<StoreLayout />}>
                <Route index element={<Home />} />
                <Route path="catalog" element={<Catalog mode="all" />} />
                <Route path="c/:slug" element={<Catalog mode="category" />} />
                <Route path="brand/:slug" element={<Catalog mode="brand" />} />
                <Route path="brands-list" element={<BrandsList />} />
                <Route path="p/:slug" element={<Product />} />
                <Route path="search" element={<Catalog mode="all" />} />
                <Route path="cart" element={<Cart />} />
                <Route path="wishlist" element={<Wishlist />} />
                <Route path="about" element={<ContentPage slug="about" />} />
                <Route path="contact" element={<Contact />} />
                <Route path="faq" element={<FAQ />} />
                <Route path="pages/:slug" element={<ContentPage />} />

                {/* Customer auth (guest-friendly screens) */}
                <Route path="account/login" element={<AccountLogin />} />
                <Route path="account/register" element={<AccountRegister />} />
                <Route path="account/forgot" element={<AccountForgot />} />
                <Route path="account/reset" element={<AccountReset />} />

                {/* Customer-only account area */}
                <Route path="account" element={<AccountLayout />}>
                  <Route index element={<AccountOverview />} />
                  <Route path="orders" element={<AccountOrders />} />
                  <Route path="orders/:id" element={<AccountOrderDetail />} />
                  <Route path="addresses" element={<AccountAddresses />} />
                  <Route path="security" element={<AccountSecurity />} />
                </Route>
              </Route>

              {/* Checkout & ThankYou — outside StoreLayout (minimal chrome) */}
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order/:id" element={<StoreLayout />}>
                <Route index element={<ThankYou />} />
              </Route>

              {/* ADMIN — lazy-loaded chunk, Suspense fallback while it streams */}
              <Route
                path="/admin/login"
                element={
                  <Suspense fallback={<AdminLoading />}>
                    <Login />
                  </Suspense>
                }
              />
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<AdminLoading />}>
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  </Suspense>
                }
              >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="products" element={<Products />} />
                <Route path="products/new" element={<ProductEdit />} />
                <Route path="products/:id" element={<ProductEdit />} />
                <Route path="categories" element={<Categories />} />
                <Route path="brands" element={<Brands />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/new" element={<OrderEdit />} />
                <Route path="orders/:id" element={<OrderEdit />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/new" element={<CustomerEdit />} />
                <Route path="customers/:id" element={<CustomerEdit />} />
                <Route path="promo-codes" element={<PromoCodes />} />
                <Route path="tools" element={<Tools />} />
                <Route path="import" element={<ImportWizard />} />
                <Route path="import/history" element={<ImportHistory />} />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute roles={["super_admin", "admin"]}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route path="logs" element={<Logs />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              {/* 404 catch-all — outside any layout */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
              </AuthProvider>
            </CustomerAuthProvider>
          </RecentlyViewedProvider>
        </WishlistProvider>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
